import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import DynamicSingleSideTab from "../../../../components/dynamicSingleSideTab/dynamicSingleSideTab";
import { useUserThemeContext } from "../../../../context/userThemeContext";
import { ScreenNamesForBigQuery } from "../../../../enums/eventsData";
import { EventsName } from "../../../../enums/eventsName";
import { LocalStorageKeys } from "../../../../enums/localStorageKeys";
import { RootState } from "../../../../redux/store/store";
import ChevronDown from "../../../../svg/chevron-down";
import { getItemFromLocalStorage } from "../../../../utils/localStorageHelper";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { swapUnderscoreHyphen } from "../../../../utils/swapUnderscoreWithHypen";
import { apiWorkerManager } from "../../../../workerManagers/workerManager";

const ExternalWorklistV2 = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    showSideNav,
    setShowSideNav,
    showWorklistNavigation,
    setShowWorklistNavigation,
  } = useUserThemeContext();

  const [sideLinks] = useState<{ id: string; label: string }[]>([
    { id: "all-pa-orders", label: "All  Orders" },
  ]);
  const [activeTab, setActiveTab] = useState<string>("");
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  useEffect(() => {
    const shouldShowSideNav = sideLinks.some((element) =>
      location.pathname.startsWith(`/external-worklist/${element.id}`),
    );

    setShowSideNav(shouldShowSideNav);
  }, [location, sideLinks]);

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
    apiWorkerManager.enqueueAndProcess({
      patient_id: "",
      org_id: getOrgIdForFetchExternalWorklist(),
      order_id: "",
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      event_name: EventsName.SIDE_NAV_CLICKED,
      additional_data: {
        screen_name: ScreenNamesForBigQuery.WORKLIST,
        source: swapUnderscoreHyphen(tabId),
      },
    });
    navigate(`/external-worklist/${tabId}`);
  };

  useEffect(() => {
    const parentTable = location.pathname.split("/").pop();
    if (parentTable && sideLinks.some((link) => link.id === parentTable)) {
      setActiveTab(parentTable);
    } else {
      const parentTableFromLocalStorgae = getItemFromLocalStorage(
        LocalStorageKeys.PARENT_TABLE,
      );
      setActiveTab(
        parentTableFromLocalStorgae
          ? parentTableFromLocalStorgae
          : "medical-pa-orders",
      );
    }
  }, [location.pathname]);

  useEffect(() => {
    showSideNav &&
      apiWorkerManager.enqueueAndProcess({
        patient_id: "",
        org_id: getOrgIdForFetchExternalWorklist(),
        order_id: "",
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        event_name: EventsName.WORKLIST_SCREEN_SHOWN,
        additional_data: {
          screen_name: ScreenNamesForBigQuery.WORKLIST,
          selected_tab: swapUnderscoreHyphen(activeTab),
        },
      });
  }, [showSideNav]);

  return (
    <div className="worklists--layout relative">
      {showSideNav && (
        <div
          className={`flex h-full ${showWorklistNavigation ? "worklists--navigation" : "worklists--navigation__hide"}`}
        >
          {showWorklistNavigation && (
            <>
              <div
                className="worklist--navigation__container flex flex-1 flex-col overflow-hidden"
                style={{
                  opacity: showWorklistNavigation ? 1 : 0,
                  transform: showWorklistNavigation
                    ? "translateX(0)"
                    : "translateX(-20px)",
                  transition:
                    "opacity 0.3s ease-in-out, transform 0.3s ease-in-out",
                }}
              >
                <div
                  className="worklists--navigation__header my-3 pl-2 text-lg font-semibold"
                  style={{
                    opacity: showWorklistNavigation ? 1 : 0,
                    transform: showWorklistNavigation
                      ? "translateY(0)"
                      : "translateY(-10px)",
                    transition:
                      "opacity 0.4s ease-in-out 0.1s, transform 0.4s ease-in-out 0.1s",
                  }}
                >
                  Orders
                </div>
                <div
                  className="worklists--navigation__body flex flex-1 flex-col gap-2 overflow-auto"
                  style={{
                    opacity: showWorklistNavigation ? 1 : 0,
                    transform: showWorklistNavigation
                      ? "translateY(0)"
                      : "translateY(10px)",
                    transition:
                      "opacity 0.5s ease-in-out 0.2s, transform 0.5s ease-in-out 0.2s",
                  }}
                >
                  {sideLinks.map((sideNavElement, index) => (
                    <div
                      key={sideNavElement.id}
                      style={{
                        opacity: showWorklistNavigation ? 1 : 0,
                        transform: showWorklistNavigation
                          ? "translateX(0)"
                          : "translateX(-15px)",
                        transition: `opacity 0.3s ease-in-out ${0.1 + index * 0.05}s, transform 0.3s ease-in-out ${0.1 + index * 0.05}s`,
                      }}
                    >
                      <DynamicSingleSideTab
                        id={sideNavElement.id}
                        label={sideNavElement.label}
                        isActive={activeTab === sideNavElement.id}
                        onTabClick={handleTabClick}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="worklist--navigation__icon relative border border-primaryGray-15">
            {" "}
            <div
              className="left-arrow--icon absolute items-center justify-center rounded-full border bg-white p-1 hover:scale-105 hover:cursor-pointer"
              style={{
                filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.25))",
                transition: "left 0.3s ease-in-out",
                left: "-14px",
                top: "14px",
              }}
              onClick={() => setShowWorklistNavigation(!showWorklistNavigation)}
            >
              <ChevronDown rotate={!showWorklistNavigation ? 270 : 90} />
            </div>
          </div>
        </div>
      )}

      <div
        className={`worklists--outlet px-3 py-6 ${showSideNav ? "" : "worklists--outlet__only"} ${!showWorklistNavigation ? "worklists--outlet__hide-left" : ""}`}
      >
        <Outlet />
      </div>
    </div>
  );
};

export default ExternalWorklistV2;
